import { useCallback, useMemo } from 'react';

import { RGB } from '@da-g3-acl/shared/common/colour-types';

import { ColourProvider } from './configuration';

export type Bounds = [min: number, max: number];

export const SingleColourProvider = (colour: RGB, bounds: Bounds): ColourProvider => {
	const [min, max] = bounds;
	return [
		{ value: min, colour: `rgb(0, 0, 0)` },
		{ value: max, colour: colour },
	];
};

export const SolidColourProvider = (colour: RGB): ColourProvider => {
	return [{ value: 0, colour: colour }];
};

export const GreyscaleProvider = (bounds: Bounds): ColourProvider => SingleColourProvider('rgb(255, 255, 255)', bounds);

export const FireGradientProvider: (bounds: Bounds) => ColourProvider = ([min, max]) => [
	{ value: min, colour: 'rgb(0, 0, 0)' },
	{ value: min + (max - min) / 5, colour: 'rgb(64, 0, 64)' },
	{ value: min + ((max - min) / 5) * 2, colour: 'rgb(212, 64, 64)' },
	{ value: min + ((max - min) / 5) * 3, colour: 'rgb(212, 128, 0)' },
	{ value: min + ((max - min) / 5) * 4, colour: 'rgb(212, 212, 0)' },
	{ value: max, colour: 'rgb(255, 255, 255)' },
];

export const toRGBObject = (rgbStr: RGB): { red: number; green: number; blue: number } => {
	const [red, green, blue] = rgbStr.match(/\d+/g)?.map(Number) ?? [0, 0, 0];
	return { red, green, blue };
};

export interface UseGradientsProps {
	getNormalisedValue: (realValue: number) => number;
	getColourForNormalisedValue: (normalisedValue: number) => RGB;
	getColourForRealValue: (realValue: number) => RGB;
	normalisedProvider: ColourProvider;
	getRealValue: (normalisedValue: number) => number;
}

export const useGradients: (provider: ColourProvider | undefined, bounds: Bounds) => UseGradientsProps = (
	provider,
	bounds,
) => {
	const sortedProvider = useMemo(
		() => (provider ?? GreyscaleProvider(bounds)).sort((a, b) => a.value - b.value),
		[bounds, provider],
	);
	// Normalise input values, and the provider to overall gradient domain such that min < val < max => 0 =< |val| =< 1
	const gradientMin = sortedProvider[0];
	const gradientMax = sortedProvider[sortedProvider.length - 1];
	const gradientLength = gradientMax.value - gradientMin.value;
	const normalisedProvider = useMemo(
		() =>
			sortedProvider.map((g) => ({
				...g,
				value: (g.value - gradientMin.value) / gradientLength,
			})),
		[gradientLength, gradientMin.value, sortedProvider],
	);

	const getColourForNormalisedValue = useCallback(
		(value: number): RGB => {
			if (normalisedProvider.length === 1) return normalisedProvider[0].colour;
			const exactThreshold = normalisedProvider.find(({ value: v }) => v === value);
			if (exactThreshold !== undefined) return exactThreshold.colour;
			const thresholdAbove = normalisedProvider.find(({ value: v }) => v > value);
			const thresholdBelow = [...normalisedProvider].reverse().find(({ value: v }) => v < value);
			if (thresholdAbove === undefined || thresholdBelow === undefined) return 'rgb(0, 0, 0)';
			const aboveRGB = toRGBObject(thresholdAbove.colour);
			const belowRGB = toRGBObject(thresholdBelow.colour);
			const ratio = (value - thresholdBelow.value) / (thresholdAbove.value - thresholdBelow.value);
			return `rgb(${Math.round(belowRGB.red + (aboveRGB.red - belowRGB.red) * ratio)}, ${Math.round(belowRGB.green + (aboveRGB.green - belowRGB.green) * ratio)}, ${Math.round(belowRGB.blue + (aboveRGB.blue - belowRGB.blue) * ratio)})`;
		},
		[normalisedProvider],
	);

	const getNormalisedValue = useCallback(
		(value: number) => (value - gradientMin.value) / gradientLength,
		[gradientLength, gradientMin.value],
	);

	const getRealValue = useCallback(
		(value: number): number => gradientMin.value + value * gradientLength,
		[gradientLength, gradientMin.value],
	);

	const getColourForRealValue = useCallback(
		(value: number): RGB => getColourForNormalisedValue(getNormalisedValue(value)),
		[getColourForNormalisedValue, getNormalisedValue],
	);

	return {
		getNormalisedValue,
		getColourForNormalisedValue,
		normalisedProvider,
		getRealValue,
		getColourForRealValue,
	};
};
